import { useEffect, useRef } from 'react';

// Ported from the site's hero: code characters light up around the cursor and fade out.
// Mouse-only and off under reduced motion; the canvas covers its positioned parent.
export const CodeFx = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    const host = cv?.parentElement;
    if (!cv || !host) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const CH = '<>/{}#$01';
    const cell = 20;
    let cols = 0;
    let rows = 0;
    let W = 0;
    let H = 0;
    let heat = new Float32Array(0);
    let glyph = new Uint8Array(0);
    let mx = -999;
    let my = -999;
    let sx = -999;
    let sy = -999;
    let inside = false;
    let running = false;
    let raf = 0;

    const setup = () => {
      W = host.clientWidth;
      H = host.clientHeight;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      cols = Math.ceil(W / cell);
      rows = Math.ceil(H / cell);
      heat = new Float32Array(cols * rows);
      glyph = new Uint8Array(cols * rows);
      for (let i = 0; i < glyph.length; i++) glyph[i] = Math.floor(Math.random() * CH.length);
    };

    const frame = () => {
      sx += (mx - sx) * 0.25;
      sy += (my - sy) * 0.25;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.font = '500 13px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const R = 150;
      let alive = false;
      if (inside) {
        const c0 = Math.max(0, Math.floor((sx - R) / cell));
        const c1 = Math.min(cols - 1, Math.ceil((sx + R) / cell));
        const r0 = Math.max(0, Math.floor((sy - R) / cell));
        const r1 = Math.min(rows - 1, Math.ceil((sy + R) / cell));
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const dx = c * cell + cell / 2 - sx;
            const dy = r * cell + cell / 2 - sy;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < R) {
              const i = r * cols + c;
              heat[i] = Math.max(heat[i], Math.pow(1 - d / R, 1.6));
            }
          }
        }
      }
      for (let i = 0; i < heat.length; i++) {
        const h = heat[i];
        if (h < 0.015) {
          heat[i] = 0;
          continue;
        }
        alive = true;
        if (Math.random() < 0.04) glyph[i] = Math.floor(Math.random() * CH.length);
        ctx.fillStyle = `rgba(255,138,61,${(h * 0.32).toFixed(3)})`;
        ctx.fillText(CH.charAt(glyph[i]), (i % cols) * cell + cell / 2, Math.floor(i / cols) * cell + cell / 2);
        heat[i] = h * 0.94;
      }
      if (alive || inside) raf = requestAnimationFrame(frame);
      else {
        running = false;
        ctx.clearRect(0, 0, W, H);
      }
    };
    const kick = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    };
    const move = (e: PointerEvent) => {
      const b = host.getBoundingClientRect();
      mx = e.clientX - b.left;
      my = e.clientY - b.top;
      if (!inside) {
        sx = mx;
        sy = my;
      }
      inside = true;
      kick();
    };
    const leave = () => {
      inside = false;
    };

    setup();
    host.addEventListener('pointermove', move);
    host.addEventListener('pointerleave', leave);
    window.addEventListener('resize', setup);
    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener('pointermove', move);
      host.removeEventListener('pointerleave', leave);
      window.removeEventListener('resize', setup);
    };
  }, []);

  return <canvas className="code-fx" ref={ref} aria-hidden="true" />;
};
